import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import axiosConn from '../../axiosConn';
import { AuthContext } from '../../context/AuthContext';
import { getPushRegistration } from './useWebPushStatus';
import {
  pushActionId,
  samePushSubscription,
  snapshotPushSubscription,
} from '../../services/webPush';

const changed = () =>
  Object.assign(new Error('Registration changed'), {
    code: 'PUSH_STATUS_REQUIRED',
  });
const unwrap = (response) => {
  if (response?.data?.success !== true) throw new Error('Test unavailable');
  return response.data.data;
};
const empty = { busy: false, attempt: null, error: '', action: '' };

// This controller owns test interactions only. Setup/claim/commit remain in the
// existing status hook. Every operation obtains a fresh read-only observation.
export default function useLocalOpsAlertTest({ jwt: suppliedJwt, enabled }) {
  const auth = useContext(AuthContext);
  const jwt = suppliedJwt === undefined ? auth?.player?.token : suppliedJwt;
  const owner = useRef({
    jwt,
    generation: 0,
    mounted: true,
    busy: false,
    selector: null,
  });
  if (owner.current.jwt !== jwt)
    owner.current = {
      jwt,
      generation: owner.current.generation + 1,
      mounted: true,
      busy: false,
      selector: null,
    };
  const [stored, setStored] = useState({ ...empty, jwt });
  useEffect(() => {
    owner.current.mounted = true;
    return () => {
      owner.current.mounted = false;
      owner.current.generation += 1;
    };
  }, [jwt]);

  const run = useCallback(
    async (action) => {
      if (!jwt || owner.current.busy) return;
      const generation = ++owner.current.generation;
      const current = () =>
        owner.current.mounted &&
        owner.current.jwt === jwt &&
        owner.current.generation === generation;
      owner.current.busy = true;
      const selector =
        action === 'send'
          ? { actionId: pushActionId() }
          : owner.current.selector;
      if (action === 'confirm' && !selector?.attemptId) {
        owner.current.busy = false;
        return;
      }
      if (action === 'send') owner.current.selector = selector;
      setStored((prior) => ({
        ...(prior.jwt === jwt ? prior : empty),
        jwt,
        busy: true,
        error: '',
        action,
        ...(action === 'send' ? { attempt: null } : {}),
      }));
      let dispatched = false;
      let checkSnapshot;
      try {
        const registration = await getPushRegistration();
        if (!current()) return;
        const snapshot = snapshotPushSubscription(
          await registration?.pushManager.getSubscription(),
        );
        if (!current()) return;
        if (!snapshot) throw changed();
        const check = async () => {
          if (!current()) return false;
          const sub = await registration.pushManager.getSubscription();
          if (!current()) return false;
          if (!samePushSubscription(sub, snapshot)) throw changed();
          return true;
        };
        checkSnapshot = check;
        const config = {
          headers: { Authorization: `Bearer ${jwt}` },
          timeout: 15000,
        };
        const observed = unwrap(
          await axiosConn.post(
            'notifications/webpush-status',
            snapshot,
            config,
          ),
        );
        if (!(await check())) return;
        if (!observed?.registered || !observed.claimObservation)
          throw changed();
        // Refreshing the proof never replaces the selected attempt, registers a
        // device, opts in, or replays a test after a timeout.
        const suffix =
          action === 'send'
            ? ''
            : action === 'confirm'
            ? '/confirm'
            : '/status';
        const body = {
          ...snapshot,
          observationProof: observed.claimObservation,
          ...(selector || {}),
        };
        dispatched = action === 'send';
        const response = await axiosConn.post(
          `notifications/test-push${suffix}`,
          body,
          config,
        );
        if (!(await check())) return;
        const attempt = unwrap(response);
        if (selector && !attempt)
          throw new Error('No result recorded for this attempt yet');
        if (
          attempt &&
          (!attempt.attemptId || typeof attempt.currentBinding !== 'boolean')
        )
          throw new Error('Invalid test status');
        if (
          attempt &&
          selector?.attemptId &&
          attempt.attemptId !== selector.attemptId
        )
          throw new Error('Wrong test status');
        if (attempt) owner.current.selector = { attemptId: attempt.attemptId };
        setStored((prior) => ({
          ...empty,
          jwt,
          attempt: attempt
            ? {
                ...attempt,
                playerReportedReceivedAt:
                  attempt.playerReportedReceivedAt ||
                  (prior.jwt === jwt &&
                  prior.attempt?.attemptId === attempt.attemptId
                    ? prior.attempt.playerReportedReceivedAt
                    : null),
              }
            : null,
        }));
      } catch (err) {
        if (!current()) return;
        if (checkSnapshot) {
          try {
            if (!(await checkSnapshot())) return;
          } catch (snapshotError) {
            err = snapshotError;
          }
        }
        const code = err?.response?.data?.code || err.code;
        const limited =
          err?.response?.status === 429 && err?.response?.data?.data;
        if (limited?.attemptId)
          owner.current.selector = { attemptId: limited.attemptId };
        setStored((prior) => ({
          ...prior,
          jwt,
          busy: false,
          action: '',
          ...(limited
            ? { attempt: limited }
            : dispatched
            ? {
                attempt: {
                  outcome: 'unknown',
                  currentBinding: ![
                    'PUSH_STATUS_REQUIRED',
                    'CLAIM_SUPERSEDED',
                    'ATTEMPT_SUPERSEDED',
                  ].includes(code),
                  reason: 'provider_unknown',
                  attemptId: null,
                },
              }
            : {}),
          error: limited
            ? ''
            : [
                'PUSH_STATUS_REQUIRED',
                'CLAIM_SUPERSEDED',
                'ATTEMPT_SUPERSEDED',
              ].includes(code)
            ? 'Alerts changed on this device. Check alerts before testing again.'
            : action === 'send' && dispatched
            ? ''
            : 'We could not check this test. Check status again; no new test was sent.',
          ...([
            'PUSH_STATUS_REQUIRED',
            'CLAIM_SUPERSEDED',
            'ATTEMPT_SUPERSEDED',
          ].includes(code) && prior.attempt
            ? { attempt: { ...prior.attempt, currentBinding: false } }
            : {}),
        }));
      } finally {
        if (current()) owner.current.busy = false;
      }
    },
    [jwt],
  );
  useEffect(() => {
    const changedRegistration = () => {
      owner.current.generation += 1;
      owner.current.busy = false;
      setStored((prior) => ({
        ...prior,
        busy: false,
        attempt: prior.attempt
          ? { ...prior.attempt, currentBinding: false }
          : null,
      }));
      if (enabled) run('status');
    };
    window.addEventListener('jg:webpush-changed', changedRegistration);
    return () =>
      window.removeEventListener('jg:webpush-changed', changedRegistration);
  }, [enabled, run]);
  useEffect(() => {
    if (enabled) run('status');
  }, [enabled, run]);
  return {
    ...(stored.jwt === jwt ? stored : empty),
    send: () => run('send'),
    check: () => run('status'),
    confirm: () => run('confirm'),
  };
}
